<template>
  <div class="form-operation-value">
    <el-row
      :gutter="20"
      type="flex"
    >
      <el-col :span="6">
        <FormItem
          rules="required"
          :label="$t('sampleDetails.measuredValues.analysisStart')"
        >
          <el-date-picker
            v-model="operationValue.analysisStart"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder=""
            :picker-options="{ disabledDate: isStartDateDisabled }"
          />
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.analysisEnd')">
          <el-date-picker
            v-model="operationValue.analysisEnd"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder=""
            :disabled="!operationValue.analysisStart"
            :picker-options="{ disabledDate: isEndDateDisabled }"
          />
        </FormItem>
      </el-col>
      <el-col
        v-if="hasValue"
        :span="6"
      >
        <FormItem :label="$t('sampleDetails.measuredValues.value')">
          <div class="value-detail">
            <template v-if="specification?.type === 'FreeText'">
              <el-input
                v-model="operationValue.value"
                :disabled="valueIsDisabled"
                @input="checkValidation"
              />
            </template>
            <template v-else-if="specification?.type === 'Selection'">
              <el-select
                v-model="operationValue.value"
                placeholder="Select"
                :disabled="valueIsDisabled"
                @change="formValidation"
              >
                <el-option
                  v-for="(item, index) in specification.selectionValues"
                  :key="index"
                  :label="item"
                  :value="item"
                ></el-option>
              </el-select>
            </template>
            <template v-else-if="specification?.type === 'Range'">
              <el-input
                type="number"
                min="0"
                v-model="operationValue.value"
                :disabled="valueIsDisabled"
                @input="checkValidation"
              />
            </template>
            <template v-else>
              <el-input
                v-model="operationValue.value"
                :disabled="valueIsDisabled"
                @input="checkValidation"
              />
            </template>
            <el-popover
              v-if="valueIsInvalid || valueIsDisabled"
              placement="top-start"
              trigger="hover"
              :content="valueErrorMessage"
            >
              <i
                slot="reference"
                class="fa fa-exclamation-triangle"
              ></i>
            </el-popover>
          </div>
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.measure')">
          <el-autocomplete
            v-model="selectedMeasure"
            style="width: 100%"
            :fetch-suggestions="measuresList"
            clearable
            value-key="label"
            placeholder=""
            @select="
              (item) => {
                operationValue.measureId = item.value;
              }
            "
            @change="
              () => {
                operationValue.measureId = null;
              }
            "
          >
            <template slot-scope="{ item }">
              <AutocompleteItem :item="item" />
            </template>
          </el-autocomplete>
        </FormItem>
      </el-col>
    </el-row>

    <el-row
      :gutter="20"
      type="flex"
    >
      <el-col :span="3">
        <FormItem :label="$t('sampleDetails.measuredValues.LD')">
          <el-input v-model="operationValue.LD" />
        </FormItem>
      </el-col>
      <el-col :span="3">
        <FormItem :label="$t('sampleDetails.measuredValues.LQ')">
          <el-input v-model="operationValue.LQ" />
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.U')">
          <el-input v-model="operationValue.U" />
        </FormItem>
      </el-col>
      <el-col
        v-if="!singleEdit"
        :span="6"
      >
        <FormItem :label="$t('sampleDetails.measuredValues.specification')">
          <el-input
            v-model="operationValue.specification"
            :disabled="!!hasSpecification"
          />
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem
          :rules="{ regex: /^\d+([.,]\d+)?$/ }"
          :label="$t('sampleDetails.measuredValues.recoveryFactor')"
        >
          <el-input
            v-model="operationValue.recoveryFactor"
            type="text"
          />
        </FormItem>
      </el-col>
    </el-row>

    <el-row
      :gutter="20"
      v-if="singleEdit"
      type="flex"
    >
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.symbol')">
          <AutocompleteSymbol
            v-model="operationValue.symbol"
            :disabled="!editableSymbolType"
          />
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.symbolType')">
          <el-select
            v-model="operationValue.symbolType"
            placeholder="Select"
            :disabled="!editableSymbolType"
          >
            <el-option
              v-for="item in symbolTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </FormItem>
      </el-col>
      <el-col :span="6">
        <FormItem :label="$t('sampleDetails.measuredValues.symbolLot')">
          <el-input
            v-model="operationValue.symbolLot"
            :disabled="!editableSymbolType"
          />
        </FormItem>
      </el-col>
    </el-row>

    <FormSpecifications
      v-if="singleEdit"
      :form-value="specificationToEdit"
      :measure="selectedMeasure"
      :disabled="operationSpecification"
    />

    <el-row
      :gutter="20"
      type="flex"
    >
      <el-col :span="24">
        <FormItem :label="$t('sampleDetails.measuredValues.notes')">
          <el-input
            type="textarea"
            v-model="operationValue.notes"
          />
        </FormItem>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import moment from 'moment';
  import lodashDebounce from 'lodash.debounce';
  import { measuresApi, analysesApi, analysesDataApi } from '@/api';
  import { SymbolTypes } from '@/config/constants';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import AutocompleteSymbol from '@/components/AutocompleteSymbol.vue';
  import FormSpecifications from '@/components/FormSpecifications.vue';
  import FormItem from './FormItem.vue';

  const specificationDefaultData = {
    type: 'None',
    selectionValues: [],
    decimalDigits: null,
    minRange: null,
    maxRange: null,
    isMinRangeInclusive: null,
    isMaxRangeInclusive: null,
    specificationValue: null,
  };

  export default {
    name: 'FormOperationValue',
    components: {
      FormItem,
      AutocompleteItem,
      AutocompleteSymbol,
      FormSpecifications,
    },
    props: {
      operationValue: { type: Object, required: true },
      sample: { type: Object, require: true },
      hasValue: { type: Boolean, default: true },
      singleEdit: { type: Boolean, default: true },
      /** @required if is single edit */
      operation: { type: Object, required: false },
      checkValue: { type: Boolean, default: false },
      /** Opt-in: allow editing the symbol type (disabled everywhere by default). */
      editableSymbolType: { type: Boolean, default: false },
    },
    data() {
      return {
        selectedMeasure: null,
        selectedThermostat: null,
        selectedTemperature: null,
        specification: { ...specificationDefaultData },
        specificationToEdit: { ...specificationDefaultData },
        valueIsInvalid: false,
        valueInvalidMessage: '',
        valueIsDisabled: false,
        valueDisabledMessage: '',
        hasInitialSpecification: false,
      };
    },
    computed: {
      symbolTypes() {
        return Object.values(SymbolTypes).map((val) => ({
          value: val,
          label: this.$t(`enums.symbolTypes.${val}`),
        }));
      },
      valueErrorMessage() {
        return `${this.valueInvalidMessage}. ${this.valueDisabledMessage}`;
      },
      operationSpecification() {
        return !!this.operation.specification;
      },
      hasSpecification() {
        return this.hasInitialSpecification;
      },
    },
    watch: {
      operation: {
        immediate: true,
        async handler(newValue) {
          if (!newValue) return;
          const { operationRegistryId, analysisId } = newValue;
          await this.fetchSpecification(analysisId, operationRegistryId);
        },
        // deep: true,
        once: true,
      },
      specification: {
        immediate: true,
        async handler(newValue) {
          if (!newValue) return;
          if (Object.keys(newValue).length > 0) {
            this.specificationToEdit = {
              ...newValue,
              selectionValues: newValue.selectionValues ?? [],
            };
            delete this.specificationToEdit?.customerProductCode;
            delete this.specificationToEdit?.customerId;
            return;
          }
          this.specificationToEdit = { ...specificationDefaultData };
        },
        // deep: true,
        once: true,
      },
      specificationToEdit: {
        handler(newValue) {
          this.$emit('update:specification', newValue);
          if (newValue) {
            this.operationValue.specificationType = newValue.type;
            this.operationValue.specificationValue =
              newValue.specificationValue;
            this.operationValue.selectionValues = newValue.selectionValues;
            this.operationValue.decimalDigits = newValue.decimalDigits;
            this.operationValue.minRange = newValue.minRange;
            this.operationValue.maxRange = newValue.maxRange;
            this.operationValue.isMinRangeInclusive =
              newValue.isMinRangeInclusive;
            this.operationValue.isMaxRangeInclusive =
              newValue.isMaxRangeInclusive;
          }
        },
        deep: true,
      },
    },
    async created() {
      // set default values
      const { measure, thermostat, temperature } = this.operationValue;
      if (measure) this.selectedMeasure = measure.description;
      if (thermostat) this.selectedThermostat = thermostat.name;
      if (temperature) this.selectedTemperature = temperature.value;

      // Reactivity fix: ensuring missing properties are reactive.
      if (!this.operationValue.symbol && this.operation?.symbol) {
        this.$set(this.operationValue, 'symbol', this.operation.symbol);
      } else if (this.operationValue.symbol === undefined) {
        this.$set(this.operationValue, 'symbol', null);
      }

      if (!this.operationValue.symbolType && this.operation?.symbolType) {
        this.$set(this.operationValue, 'symbolType', this.operation.symbolType);
      } else if (this.operationValue.symbolType === undefined) {
        this.$set(this.operationValue, 'symbolType', null);
      }

      if (this.operationValue.symbolLot === undefined) {
        this.$set(this.operationValue, 'symbolLot', null);
      }

      // Set hasInitialSpecification based on operationValue.specification
      this.hasInitialSpecification = !!this.operationValue.specification;

      // check if value is invalid
      this.formValidation(this.operationValue.value);
      // check if a value can be entered
      if (this.operation) {
        const operationRegistryIds = [];
        operationRegistryIds.push(this.operation.operationRegistryId);
        const response = await analysesDataApi.checkValueInsertion(
          this.operation.labSampleAnalysisId ??
            this.operationValue.labSampleAnalysisId,
          operationRegistryIds
        );
        this.valueIsDisabled =
          !response.data[this.operation.operationRegistryId].allowed;
        if (this.valueIsDisabled) {
          this.valueDisabledMessage =
            response.data[this.operation.operationRegistryId].reason || '';
        }
      }
    },
    methods: {
      async fetchSpecification(analysisId, operationId) {
        if (
          this.operation &&
          this.operation.value &&
          this.operation.value.specificationType &&
          this.operation.value.specificationType !== 'None'
        ) {
          const {
            specificationType,
            selectionValues,
            decimalDigits,
            minRange,
            maxRange,
            isMinRangeInclusive,
            isMaxRangeInclusive,
            specificationValue,
          } = this.operation.value;
          this.specification = {
            type: specificationType,
            selectionValues: selectionValues ?? [],
            decimalDigits,
            minRange,
            maxRange,
            isMinRangeInclusive,
            isMaxRangeInclusive,
            specificationValue,
          };
          return;
        }
        const specificationsList = await analysesApi.specificationsShow(
          analysisId,
          operationId
        );
        const { data } = specificationsList;
        // const [specification] = data;
        const sample = this.sample[0] ?? this.sample;
        const specification =
          data.find(
            (spec) =>
              (spec?.customerId === null ||
                spec?.customerId === sample?.customerId) &&
              (spec?.customerProductCode === null ||
                spec?.customerProductCode === sample?.productCode)
          ) || null;
        this.specification = specification || {};
      },
      async measuresList(value, cb) {
        const response = await measuresApi.autocompleteFetch(value);
        const measures = response.data.map((item) => ({
          label: item.description,
          value: item.id,
        }));
        cb(measures);
      },
      isStartDateDisabled(date) {
        const today = moment.utc().startOf('day').toDate();
        if (date > today) return true;

        if (this.sample.acceptedAt) {
          const acceptedAt = moment(this.sample.acceptedAt)
            .startOf('day')
            .toDate();
          if (date < acceptedAt) return true;
        }
        return false;
      },
      isEndDateDisabled(date) {
        const today = moment.utc().startOf('day').toDate();
        if (date > today) return true;

        const startDate = moment(this.operationValue.analysisStart)
          .startOf('day')
          .toDate();
        if (date < startDate) return true;

        return false;
      },
      checkValidation: lodashDebounce(function checkValidation(value) {
        this.formValidation(value);
      }, 800),
      async formValidation(value) {
        if (
          this.operation &&
          this.operation.labSampleAnalysisId &&
          this.operation.operationRegistryId
        ) {
          const validation = await analysesApi.validateOperation({
            id: this.operation.id,
            labSampleAnalysisId: this.operation.labSampleAnalysisId,
            operationRegistryId: this.operation.operationRegistryId,
            value,
          });
          this.valueIsInvalid = !validation.data.isValid;
        }
        if (this.valueIsInvalid) {
          this.valueInvalidMessage = this.$t(
            'sampleDetails.measuredValues.valueWarningMessage'
          );
        }
      },
    },
  };
</script>

<style lang="scss">
  .form-operation-value {
    .value-detail {
      display: flex;
      align-items: center;

      i {
        margin-left: 10px;
      }
    }
  }
</style>
